import {Button} from 'antd';
import Spin from 'antd/es/spin';
import cn from 'classnames';
import React, {
    FC,
    useEffect,
    useRef, useState,
} from 'react';

import {ActionBar} from 'components/@common/widgets/action-bar';
import {CustomCard} from 'components/@common/widgets/custom-card';
import {ParametersFilter} from 'components/@common/widgets/parameters-filter';
import {IParametersFilterRef} from 'components/@common/widgets/parameters-filter/parameters-filter';
import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {UIBlocker} from 'components/ui-blocker';
import {ReactComponent as ReloadOutlined} from 'shared/assets/reload.svg';
import {ReactComponent as TrashIcon} from 'shared/assets/trash-x.svg';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {omitEmptyLines} from 'shared/utils';
import {useQueryParams} from 'shared/utils/query-params';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {externalLoadingSliceActions} from 'store/slices/external-loading/external-loading';
import {loadOpenInterface} from 'store/slices/external-loading/external-loading-thunks';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {useGenerateFilters} from '../../hooks';
import {OPEN_INTERFACE_ROLE_SECTION} from '../../model/constants/open-interface-constants';
import {
    TOpenInterfaceParametersFilter,
    IOpenInterfaceParametersFilterForm,
} from '../../model/types/open-interface-types';
import {ClearStageModal} from '../clear-stage-modal';
import {OpenInterfaceTable, IOpenInterfaceTableRef} from '../open-interface-table';
import styles from './open-interface.module.less';

interface IOpenInterfaceProps {}

export const OpenInterfacePage: FC<IOpenInterfaceProps> = () => {
    const dispatch = useAppDispatch();
    const {getQueryParam, removeQueryParam} = useQueryParams();
    const loadIdQueryParam = getQueryParam('loadId');
    const scenarioIdQueryParam = getQueryParam('scenarioId');
    const srcIdQueryParam = getQueryParam('srcId');

    const {resetOpenInterfaceData} = externalLoadingSliceActions;

    const [openInterfaceParametersFilter, setOpenInterfaceParametersFilter] = useState<TOpenInterfaceParametersFilter>();

    const isLoadingOpenInterfaceData = useAppSelector(s => selectIsThunkPending(
        s, loadOpenInterface.typePrefix,
    ));

    const clearStageConfirmationModalRef = useRef<IModalOpenerComponentRef>(null);
    const openInterfaceTableRef = useRef<IOpenInterfaceTableRef>(null);
    const parametersFilterRef = useRef<IParametersFilterRef<IOpenInterfaceParametersFilterForm>>(null);
    const [isNeedFetchTableData, setIsNeedFetchTableData] = useState(false);
    const [shouldRefetchScenarios, setShouldRefetchScenarios] = useState(false);

    const {generateFilters, setScenarioId, setLoadId} = useGenerateFilters({
        parametersFilterRef,
        shouldRefetchScenarios,
        setShouldRefetchScenarios,
    });

    useEffect(() => {
        const requiredParams = [loadIdQueryParam, scenarioIdQueryParam, srcIdQueryParam].map(param => param?.trim());
        const hasAllRequiredParams = requiredParams.every(param => param?.length && Number.isFinite(Number(param)));

        if (hasAllRequiredParams) {
            const scenarioId = Number(scenarioIdQueryParam);
            const loadId = Number(loadIdQueryParam);
            const srcId = Number(srcIdQueryParam);

            parametersFilterRef.current?.setInitialFormValues({scenarioId, loadId, srcId});
            setScenarioId(scenarioId);
            setLoadId(loadId);
            removeQueryParam('scenarioId', {action: 'replace'});
            removeQueryParam('loadId', {action: 'replace'});
            removeQueryParam('srcId', {action: 'replace'});
        }
    }, [parametersFilterRef, loadIdQueryParam, scenarioIdQueryParam, setLoadId, setScenarioId, removeQueryParam]);

    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: [OPEN_INTERFACE_ROLE_SECTION],
    });

    useEffect(() => () => {
        dispatch(resetOpenInterfaceData());
    }, []);

    const shouldShowActionBar = hasUserRoleFunction(EUserRoleFunction.DELETE);

    const resetState = () => {
        dispatch(resetOpenInterfaceData());
        setScenarioId(undefined);
        setLoadId(undefined);
        setShouldRefetchScenarios(true);

        parametersFilterRef.current?.setInitialFormValues(undefined);
    };

    return (
        <div className={cn(styles['open-interface'])}>
            <CustomCard
                isLoading={false}
                accordionTransitionType="1"
                title="Открытый интерфейс данных"
                accordionContent={(
                    <ParametersFilter<IOpenInterfaceParametersFilterForm>
                        ref={parametersFilterRef}
                        onChange={values => {
                            setOpenInterfaceParametersFilter(omitEmptyLines(values) as TOpenInterfaceParametersFilter);
                        }}
                        onClear={resetState}
                        className={cn(styles['open-interface__filters'])}
                        changeFilterParametersText="Скорректировать параметры"
                        clearFilterParametersText="Сбросить"
                        clearFilterParametersIcon={<ReloadOutlined />}
                    >
                        {generateFilters}
                    </ParametersFilter>
                )}
                isAccordionContentVisibleByDefault
            >
                {shouldShowActionBar && (
                    <ActionBar>
                        {hasUserRoleFunction(EUserRoleFunction.DELETE) && (
                            <>
                                <ClearStageModal
                                    ref={clearStageConfirmationModalRef}
                                    loadId={openInterfaceParametersFilter?.loadId}
                                    resetState={resetState}
                                />
                                <Button
                                    type="primary"
                                    className="button-critic"
                                    icon={<TrashIcon />}
                                    onClick={() => {
                                        clearStageConfirmationModalRef.current?.showModal();
                                    }}
                                    disabled={!openInterfaceParametersFilter?.loadId}
                                >
                                    Очистить stage-область
                                </Button>
                            </>
                        )}
                    </ActionBar>
                )}
                <Spin
                    spinning={isLoadingOpenInterfaceData}
                    tip="Загрузка данных..."
                >
                    <UIBlocker
                        thunkError={{
                            verbose: true,
                        }}
                    >
                        <OpenInterfaceTable
                            openInterfaceParametersFilter={openInterfaceParametersFilter}
                            ref={openInterfaceTableRef}
                            isNeedFetchTableData={isNeedFetchTableData}
                            setIsNeedFetchTableData={setIsNeedFetchTableData}
                        />
                    </UIBlocker>
                </Spin>
            </CustomCard>
        </div>
    );
};
